import { toast as reactToast } from 'react-toastify';
import { CustomToast } from './CustomToast';

// Modern toast utility with custom styling
class ModernToast {
  // Default options for all toasts
  static defaultOptions = {
    position: 'top-right',
    autoClose: 4000,
    hideProgressBar: true,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
    progress: undefined,
    closeButton: false,
    icon: false,
    style: {
      backgroundColor: 'transparent',
      boxShadow: 'none',
      padding: 0,
      minHeight: 'auto',
    },
    bodyStyle: {
      padding: 0,
      margin: 0,
    },
    className: 'modern-toast-container',
  };

  // Success toast
  static success(message, options = {}) {
    const mergedOptions = { ...this.defaultOptions, ...options };
    
    return reactToast(
      ({ closeToast }) => (
        <CustomToast 
          type="success" 
          message={message} 
          onClose={closeToast} 
        />
      ),
      mergedOptions
    );
  }

  // Error toast
  static error(message, options = {}) {
    const mergedOptions = { 
      ...this.defaultOptions, 
      autoClose: 6000, // Keep error messages longer
      ...options 
    };
    
    return reactToast(
      ({ closeToast }) => (
        <CustomToast 
          type="error" 
          message={message} 
          onClose={closeToast} 
        />
      ),
      mergedOptions
    );
  }

  // Warning toast
  static warning(message, options = {}) {
    const mergedOptions = { ...this.defaultOptions, ...options };
    
    return reactToast(
      ({ closeToast }) => (
        <CustomToast 
          type="warning" 
          message={message} 
          onClose={closeToast} 
        />
      ),
      mergedOptions
    );
  }

  // Info toast
  static info(message, options = {}) {
    const mergedOptions = { ...this.defaultOptions, ...options };
    
    return reactToast(
      ({ closeToast }) => (
        <CustomToast 
          type="info" 
          message={message} 
          onClose={closeToast} 
        />
      ),
      mergedOptions
    );
  }

  // Loading toast (can be updated)
  static loading(message = 'Processing...', options = {}) {
    const mergedOptions = { 
      ...this.defaultOptions, 
      autoClose: false,
      closeOnClick: false,
      draggable: false,
      ...options 
    };
    
    return reactToast(
      ({ closeToast }) => (
        <CustomToast 
          type="info" 
          message={message} 
          onClose={closeToast} 
        />
      ),
      mergedOptions
    );
  }

  // Update existing toast (useful for loading -> success/error flow)
  static update(toastId, { type, message, ...options }) {
    const mergedOptions = { 
      ...this.defaultOptions,
      autoClose: type === 'error' ? 6000 : 4000,
      ...options 
    };

    return reactToast.update(toastId, {
      render: ({ closeToast }) => (
        <CustomToast 
          type={type} 
          message={message} 
          onClose={closeToast} 
        />
      ),
      ...mergedOptions,
    });
  }

  // Dismiss specific toast
  static dismiss(toastId) {
    return reactToast.dismiss(toastId);
  }

  // Dismiss all toasts
  static dismissAll() {
    return reactToast.dismiss();
  }
}

// Create convenience methods that match the original toast API
export const toast = {
  success: (message, options) => ModernToast.success(message, options),
  error: (message, options) => ModernToast.error(message, options),
  warning: (message, options) => ModernToast.warning(message, options),
  info: (message, options) => ModernToast.info(message, options),
  loading: (message, options) => ModernToast.loading(message, options),
  update: (toastId, config) => ModernToast.update(toastId, config),
  dismiss: (toastId) => ModernToast.dismiss(toastId),
  dismissAll: () => ModernToast.dismissAll(),
};

export default toast;
